// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { transferExam } from "../api";

export const useTransferExam = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (inputData: {
      patientId: string;
      examId: string | undefined;
      newPatientId: string;
    }) => {
      if (!clinicId || !inputData.examId) throw new Error();

      try {
        const token = await getToken();
        return transferExam(
          token,
          clinicId,
          inputData.patientId,
          inputData.examId,
          inputData.newPatientId
        );
      } catch (error) {
        throw error;
      }
    },
    // Invalidate query cache
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exams"] });
      queryClient.invalidateQueries({ queryKey: ["scheduled-exams"] });
      queryClient.invalidateQueries({ queryKey: ["linked-exams"] });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Exam transferred successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Exam could not be transferred!"),
      });
    },
  });
};
